/**
 * Authentication context — provides auth state to the entire app.
 *
 * Handles:
 * - JWT persistence in localStorage
 * - Auto-validation on app load (calls /api/auth/me)
 * - Login (store JWT after magic link verification)
 * - Logout (invalidate session, clear JWT)
 * - Include JWT in API calls via Authorization header
 *
 * Auth is OPTIONAL — the app works without logging in.
 * Auth unlocks cloud features (settings sync, etc).
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { getApiUrl } from '../utils/environment';
import { setCfCookies, clearCfCookies } from '../utils/cfCookies';

// ── Types ──

interface User {
  id: number;
  email: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (jwt: string, user: User, cfCookies?: unknown) => void;
  logout: () => Promise<void>;
  getAuthHeaders: () => Record<string, string>;
}

// ── Constants ──

const JWT_STORAGE_KEY = 'talkyn-auth-jwt';
const USER_STORAGE_KEY = 'talkyn-auth-user';

// ── Helpers ──

function getApiBaseUrl(): string {
  return getApiUrl();
}

function getStoredJwt(): string | null {
  return localStorage.getItem(JWT_STORAGE_KEY);
}

function getStoredUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ── Context ──

const AuthContext = createContext<AuthState>({
  user: null,
  isAuthenticated: false,
  loading: true,
  login: () => {},
  logout: async () => {},
  getAuthHeaders: () => ({}),
});

// ── Provider ──

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(getStoredUser);
  const [loading, setLoading] = useState(true);

  // Validate stored JWT on mount
  useEffect(() => {
    const jwt = getStoredJwt();
    if (!jwt) {
      setLoading(false);
      return;
    }

    const baseUrl = getApiBaseUrl();
    fetch(`${baseUrl}/api/auth/me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error('Invalid token');
      })
      .then((data) => {
        setUser(data.user);
        localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
        // Fetch CF cookies for the session
        return fetch(`${baseUrl}/api/auth/cf-cookies`, {
          headers: { Authorization: `Bearer ${jwt}` },
        }).then((r) => (r.ok ? r.json() : null));
      })
      .then((cfData) => {
        if (cfData?.cfCookies) setCfCookies(cfData.cfCookies);
      })
      .catch(() => {
        // Token is invalid — clear stored auth
        localStorage.removeItem(JWT_STORAGE_KEY);
        localStorage.removeItem(USER_STORAGE_KEY);
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((jwt: string, userData: User, cfCookies?: unknown) => {
    localStorage.setItem(JWT_STORAGE_KEY, jwt);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(userData));
    setUser(userData);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setCfCookies((cfCookies as any) ?? null);
  }, []);

  const logout = useCallback(async () => {
    const jwt = getStoredJwt();
    if (jwt) {
      const baseUrl = getApiBaseUrl();
      try {
        await fetch(`${baseUrl}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${jwt}` },
        });
      } catch {
        // Ignore — still clear local state
      }
    }
    localStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setUser(null);
    clearCfCookies();
  }, []);

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const jwt = getStoredJwt();
    return jwt ? { Authorization: `Bearer ${jwt}` } : {};
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        loading,
        login,
        logout,
        getAuthHeaders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ── Hook ──

export function useAuth(): AuthState {
  return useContext(AuthContext);
}
